// @flow
import * as React from 'react';
import { graphql } from 'gatsby';
import compose from 'recompose/compose';
import withStyles, {
  type WithStyles,
} from '@plugins/material-ui/hocs/withStyles';
import type { Theme } from '@material-ui/core/styles';

import NodeBody from '~plugins/prismic/components/NodeBody';
import useGroupedSlicesByType from '~plugins/prismic/hooks/useGroupedSlicesByType';
import withNodePage, {
  type WithNodePageProps,
} from '~plugins/prismic/hocs/withNodePage';
import Header from '~components/Header';
import FooterContactForm from '~components/FooterContactForm';
import type { PrismicPartnerPage } from '~schema';
import useAllNodeBodyComponents from '~hooks/useAllNodeBodyComponents';

import { SLICE_TABBED_CARD_GROUPED_NAME } from '../constants';

export type ClassKey = 'root' | 'bodySection';

export type Props = {
  ...$Exact<WithStyles<ClassKey>>,
  ...$Exact<WithNodePageProps<PrismicPartnerPage>>,
};

const styles = (unusedTheme: Theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    overflow: 'hidden',
  },
  bodySection: {},
});

const nodeBodyComponents = useAllNodeBodyComponents();

const PartnerPage = ({ classes, node }: Props) => {

  const body = useGroupedSlicesByType({
    slices: node?.data?.body,
    sliceType: 'tabbed_cards',
    groupedType: SLICE_TABBED_CARD_GROUPED_NAME,
  });

  return (
    <div className={classes.root}>
      <Header
        componentVariant="complex"
        title={node?.data?.title}
        subtitle={node?.data?.subtitle}
        partnerTitleImage={node?.data?.partner_title_image}
        backgroundImage={node?.data?.background_image}
      />
      <NodeBody
        node={node}
        body={body}
        components={nodeBodyComponents}
        className={classes.bodySection}
      />
      <FooterContactForm currentPage={node?.data?.name?.text} />
    </div>
  );
};

export default compose(
  withNodePage<PrismicPartnerPage, *>({
    getNode: data => data?.prismicPartnerPage,
  }),
  withStyles<ClassKey, *, Props>(styles, { name: 'PartnerPage' }),
)(PartnerPage);

export const query = graphql`
  query PartnerPageQuery($prismicId: ID) {
    prismicPartnerPage(prismicId: { eq: $prismicId }) {
      id
      lang
      uid
      first_publication_date
      last_publication_date
      data {
        meta_title {
          text
        }
        meta_description {
          text
        }
        meta_keywords {
          meta_keyword {
            text
          }
        }
        meta_image {
          alt
          url
          dimensions {
            width
            height
          }
        }
        name {
          text
        }
        title {
          text
          html
        }
        subtitle {
          text
          html
        }
        background_image {
          alt
          fluid(maxWidth: 1200) {
            ...GatsbyPrismicImageFluid_withWebp_noBase64
          }
        }
        partner_title_image {
          alt
          fluid(maxWidth: 300) {
            ...GatsbyPrismicImageFluid_withWebp_noBase64
          }
        }
        body {
          ... on Node {
            id
          }
          ... on PrismicPartnerPageBodyTextWithImage {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_image {
                alt
                fluid(maxWidth: 500) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_video {
                url
                link_type
              }
              slice_background_color
              overlapping_image
              image_position
            }
            items {
              item_highlighted_text {
                text
                html
              }
            }
          }
          ... on PrismicPartnerPageBodyItemsList {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_image {
                alt
                fluid(maxWidth: 600) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_color
              overlapping_image
              image_position
            }
            items {
              item_name {
                text
                html
              }
              item_link {
                link_type
                target
                url
              }
              item_icon {
                alt
                localFile {
                  id
                  childImageSharp {
                    fluid(maxWidth: 50, quality: 85) {
                      ...GatsbyImageSharpFluid_withWebp_noBase64
                    }
                  }
                }
              }
            }
          }
          ... on PrismicPartnerPageBodyHighlightedItems {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
            }
            items {
              item_title {
                text
                html
              }
              item_description {
                text
                html
              }
              item_icon {
                alt
                localFile {
                  id
                  childImageSharp {
                    fluid(maxWidth: 70, quality: 85) {
                      ...GatsbyImageSharpFluid_withWebp_noBase64
                    }
                  }
                }
              }
            }
          }
          ... on PrismicPartnerPageBodySingleImage {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_image {
                alt
                fluid(maxWidth: 1000) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_caption {
                text
                html
              }
              image_width
            }
          }
          ... on PrismicPartnerPageBodyImageGrid {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              image_width
              max_line_items
            }
            items {
              item_image {
                alt
                fluid(maxWidth: 500) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              item_caption {
                text
                html
              }
            }
          }
          ... on PrismicPartnerPageBodyVideo {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_link {
                link_type
                url
              }
              video_width
              autoplay
              caption {
                text
                html
              }
              slice_image_placeholder {
                alt
                fluid(maxWidth: 1000) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicPartnerPageBodyHighlightedContent {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
              image_position
            }
            items {
              item_title {
                text
                html
              }
              item_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_image {
                alt
                fluid(maxWidth: 500) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicPartnerPageBodyRelatedContent {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_link_label {
                text
              }
              slice_link {
                link_type
                target
                url
              }
              slice_background_color
            }
            items {
              item_title {
                text
                html
              }
              item_description {
                text
                html
              }
              item_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_image {
                alt
                fluid(maxWidth: 300) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicPartnerPageBodyBanner {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_overlapping_image {
                alt
                fluid(maxWidth: 400) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_image {
                alt
                fluid(maxWidth: 800) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_image_color
              slice_background_color
            }
            items {
              item_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_link_style
            }
          }
          ... on PrismicPartnerPageBodyDownloadForm {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_document {
                link_type
                url
                target
                document {
                  ... on PrismicWhitePaper {
                    id
                    data {
                      document_link {
                        url
                      }
                    }
                  }
                }
              }
              slice_background_color
              zoho_listkey
              zoho_lead_source
              zoho_industry
            }
          }
          ... on PrismicPartnerPageBodySolutions {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
            }
            items {
              item_title {
                text
                html
              }
              item_description {
                text
                html
              }
              item_link_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_background_image {
                alt
                fluid(maxWidth: 960) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicPartnerPageBodyLinksGrid {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
            }
            items {
              item_link_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
            }
          }
          ... on PrismicPartnerPageBodyTitleAndDescription {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_title_image {
                alt
                fluid(maxWidth: 300) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_color
              container_width
            }
          }
          ... on PrismicPartnerPageBodyTabbedCards {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_tab_name {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_image {
                alt
                fluid(maxWidth: 500) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_color
            }
            items {
              item_name {
                text
                html
              }
              item_link {
                link_type
                target
                url
              }
              item_icon {
                alt
                localFile {
                  id
                  childImageSharp {
                    fluid(maxWidth: 50, quality: 85) {
                      ...GatsbyImageSharpFluid_withWebp_noBase64
                    }
                  }
                }
              }
            }
          }
          ... on PrismicPartnerPageBodyArViewer {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_glb_model_link {
                link_type
                url
              }
              select_usdz_model_link {
                link_type
                url
              }
              slice_background_image {
                alt
                fluid(maxWidth: 500) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_color
              viewer_button
            }
            items {
              item_title {
                text
                html
              }
              item_description {
                text
                html
              }
              item_icon {
                alt
                localFile {
                  id
                  childImageSharp {
                    fluid(maxWidth: 50, quality: 85) {
                      ...GatsbyImageSharpFluid_withWebp_noBase64
                    }
                  }
                }
              }
            }
          }
          ... on PrismicPartnerPageBodyPrice {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_price {
                text
              }
              slice_image {
                alt
                fluid(maxWidth: 500) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              slice_background_image_color
              slice_background_color
              overlapping_image
            }
            items {
              item_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_link_style
            }
          }
          ... on PrismicPartnerPageBodyPrices {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
            }
            items {
              item_title {
                text
                html
              }
              item_description {
                text
                html
              }
              item_price {
                text
              }
              item_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_image {
                alt
                fluid(maxWidth: 300) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicPartnerPageBodyEmbeddedContent {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_embedded_content {
                text
                html
              }
            }
          }
          ... on PrismicPartnerPageBodyAnimation {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
            }
          }
          ... on PrismicPartnerPageBodyCallToAction {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
            }
            items {
              item_link_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_link_variant
            }
          }
          ... on PrismicPartnerPageBodyButtonCards {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
            }
            items {
              item_title {
                text
              }
              item_description {
                text
                html
              }
              item_link {
                link_type
                target
                url
              }
              item_image {
                alt
                fluid(maxWidth: 400) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicPartnerPageBodyContentGrid {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              slice_background_color
            }
            items {
              item_title {
                text
                html
              }
              item_description {
                text
                html
              }
              item_label {
                text
              }
              item_link {
                link_type
                target
                url
              }
              item_image {
                alt
                fluid(maxWidth: 300) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
            }
          }
          ... on PrismicPartnerPageBodyFloatingButton {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_label {
                text
              }
              slice_link {
                link_type
                target
                url
              }
            }
          }
          ... on PrismicPartnerPageBodyIconsGrid {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
              max_line_items
            }
            items {
              item_icon {
                alt
                fluid(maxWidth: 300) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              item_name {
                text
                html
              }
              item_link {
                link_type
                target
                url
              }
            }
          }
          ... on PrismicPartnerPageBodyIconsCarousel {
            id
            slice_type
            primary {
              html_id
              html_class
              html_css {
                text
                html
              }
              slice_title {
                text
                html
              }
              slice_description {
                text
                html
              }
            }
            items {
              item_icon {
                alt
                fluid(maxWidth: 300) {
                  ...GatsbyPrismicImageFluid_withWebp_noBase64
                }
              }
              item_name {
                text
                html
              }
              item_link {
                link_type
                target
                url
              }
            }
          }
        }
      }
    }
  }
`;
